import React, { Children, cloneElement, useCallback, useEffect, useId, useRef, useState } from "react";
import "./HorizontalScroll.css";

// Keep the original scroll container (and its table/grid/flex layout) intact.
export default function HorizontalScroll({ children, label, className = "" }) {
  const child = Children.only(children);
  const generatedId = useId();
  const viewportId = child.props.id || `horizontal-scroll-${generatedId}`;
  const viewportRef = useRef(null);
  const childRef = child.ref;
  const setRef = useCallback((node) => {
    viewportRef.current = node;
    if (typeof childRef === "function") childRef(node);
    else if (childRef) childRef.current = node;
  }, [childRef]);
  const [edges, setEdges] = useState({ overflow: false, left: false, right: false });

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    let frame;
    const measure = () => {
      const max = viewport.scrollWidth - viewport.clientWidth;
      const next = {
        overflow: viewport.clientWidth > 0 && max > 2,
        left: viewport.scrollLeft > 2,
        right: viewport.scrollLeft < max - 2,
      };
      setEdges((previous) => Object.keys(next).every((key) => previous[key] === next[key]) ? previous : next);
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    const observeContent = () => {
      resize?.disconnect();
      resize?.observe(viewport);
      Array.from(viewport.children).forEach((element) => resize?.observe(element));
      schedule();
    };
    // Async catalog results, chip counts, images and table contents may change
    // the scroll width without changing the viewport's own dimensions.
    const mutations = new MutationObserver(observeContent);
    mutations.observe(viewport, { childList: true, subtree: true, characterData: true, attributes: true });
    observeContent();
    viewport.addEventListener("scroll", schedule, { passive: true });
    viewport.addEventListener("load", schedule, true);
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(frame);
      resize?.disconnect();
      mutations.disconnect();
      viewport.removeEventListener("scroll", schedule);
      viewport.removeEventListener("load", schedule, true);
      window.removeEventListener("resize", schedule);
    };
  }, []);

  const move = (direction) => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    viewport.scrollBy({ left: direction * viewport.clientWidth * 0.8, behavior: reduceMotion ? "instant" : "smooth" });
  };

  return (
    <div className={`ik-horizontal-scroll${edges.overflow ? " has-overflow" : ""} ${className}`}>
      {cloneElement(child, {
        ref: setRef,
        id: viewportId,
        className: `${child.props.className || ""} ik-horizontal-scroll__viewport`,
        tabIndex: child.props.tabIndex ?? 0,
        role: child.props.role || "region",
        "aria-label": child.props["aria-label"] || label,
      })}
      {edges.overflow && [-1, 1].map((direction) => (
        <button key={direction} type="button"
          className={`ik-horizontal-scroll__handle ik-horizontal-scroll__handle--${direction < 0 ? "left" : "right"}`}
          aria-label={`${label} ${direction < 0 ? "왼쪽" : "오른쪽"}으로 이동`}
          aria-controls={viewportId}
          disabled={direction < 0 ? !edges.left : !edges.right}
          onClick={() => move(direction)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d={direction < 0 ? "m15 6-6 6 6 6" : "m9 6 6 6-6 6"} />
          </svg>
        </button>
      ))}
    </div>
  );
}
